import { EditMode, ExpressEditMode, PatientTab, ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { ProfilePicture } from "@/Common/Models/UI/ProfilePicture";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfileHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.Profile.ChangeProfileTabs, this.changeProfileTab);
        this.RegisterOperation(LocalPatientOperations.Profile.AddMemoClick, this.AddMemoClick);
        this.RegisterOperation(LocalPatientOperations.Profile.CancelMemoClick, this.CancelMemoClick);
        this.RegisterOperation(LocalPatientOperations.Profile.CancelEditMemoClick, this.CancelEditMemoClick);
        this.RegisterOperation(LocalPatientOperations.Profile.DeleteMemoClick, this.DeleteMemoClick);
        this.RegisterOperation(LocalPatientOperations.Profile.SaveMemoClick, this.SaveMemoClick);
        this.RegisterOperation(LocalPatientOperations.Profile.EditMemoClick, this.EditMemoClick);
        this.RegisterOperation(LocalPatientOperations.Profile.PrintSummary, this.PrintSummary);
        this.RegisterOperation(LocalPatientOperations.Profile.ImageUpload_Open, this.ToggleImageUploadPopup);
        this.RegisterOperation(LocalPatientOperations.Profile.ImageUpload_Cancel, this.ToggleImageUploadPopup);
        this.RegisterOperation(LocalPatientOperations.Profile.ImageUpload_Completed, this.ImageUploadCompleted);
        this.RegisterOperation(LocalPatientOperations.Profile.CloseDropDown, this.CloseDropDown);
        this.RegisterOperation(LocalPatientOperations.Profile.NAMLabelClick, this.NAMLabelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.DOBLabelClick, this.DOBLabelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.PhoneLabelClick, this.PhoneLabelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.EmailLabelClick, this.EmailLabelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.PatientFirstNameLabelClick, this.PatientFirstNameLabelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.PatientLastNameLabelClick, this.PatientLastNameLabelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.PatientGenderLabelClick, this.PatientGenderLabelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.PreferredIntervenorOnFileClick, this.PreferredIntervenorOnFileClick);

        this.RegisterOperation(LocalPatientOperations.Profile.EnrollmentStatusLabelClick, this.EnrollmentStatusLabelCLick);
        this.RegisterOperation(LocalPatientOperations.Profile.PatientBanner_ShowPopulationBasedFundingTab, this.showPopulationBasedFundingTab);
        this.RegisterOperation(LocalPatientOperations.Profile.AddressLabelClick, this.AddressLabelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.ClearBannerExpressEdit, this.ClearExpressEdit);
        this.RegisterOperation(LocalPatientOperations.Profile.ClearBannerExpressEdit, this.ClearExpressEdit);
        this.RegisterOperation(LocalPatientOperations.Profile.DeactivatePatientClick, this.DeactivatePatientTogglePopup);
        this.RegisterOperation(LocalPatientOperations.Profile.DeactivatePatientCancelClick, this.DeactivatePatientTogglePopup);
        this.RegisterOperation(LocalPatientOperations.Profile.DeactivatePatientSaveClick, this.DeactivatePatient);
        this.RegisterOperation(LocalPatientOperations.Profile.DeactivatePatientNoteChange, this.DeactivatePatientNoteChange);
        this.RegisterOperation(LocalPatientOperations.Profile.DeactivatePatientReasonChange, this.DeactivatePatientReasonChange);
        this.RegisterOperation(LocalPatientOperations.UI.TogglePatientHeaderDropdownMenu, this.TogglePatientHeaderDropdownMenu);
        this.RegisterOperation(LocalPatientOperations.UI.TogglePatientMergeRequestPopup, this.togglePatientMergeRequestPopup);
        this.RegisterOperation(LocalPatientOperations.UI.ToggleCancelPatientMergeRequestPopup, this.toggleCancelPatientMergeRequestPopup);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.OnOverlayContainerClick, false);
        this.RegisterOperation(LocalPatientOperations.Profile.DeleteProfilePic, this.deleteProfilePic);
        this.RegisterOperation(LocalPatientOperations.Profile.ShowAddNewExternalMessagePopup, this.showNewExternalMessagePopup);
        this.RegisterOperation(LocalPatientOperations.Profile.ShowRedActiveNoteTab, this.showRedActiveNoteTab);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_UpdateMandatoryFields, this.updatePatientMandatoryFields);
        this.RegisterOperation(LocalPatientOperations.Profile.UpdatePatientMergeStatus, this.handlePatientMergeRequestResponse);
        this.RegisterOperation(LocalPatientOperations.Profile.UpdateCancelPatientMergeStatus, this.handlePatientMergeCancellationRequestResponse);
        this.RegisterOperation(LocalPatientOperations.Profile.MarkTestPatientClick, this.MarkTestPatientTogglePopup);
        this.RegisterOperation(LocalPatientOperations.Profile.MarkTestPatientCancelClick, this.MarkTestPatientTogglePopup);
        this.RegisterOperation(LocalPatientOperations.Profile.MarkTestPatientSaveClick, this.MarkTestPatient);
    }

    private CloseDropDown(request: LocalEvent, store: PatientStore): void {
        let bannerModel = store.PatientBannerModels[request.PatientId];
        bannerModel = bannerModel.set("PatientDropdownVisible", false);
        store.PatientBannerModels[request.PatientId] = bannerModel;
    }

    private ImageUploadCompleted(request: LocalEvent, store: PatientStore): void {
        store.ActionsProfile.saveProfilePic(request.Get<string>("path"), request.PatientId);
    }

    private ToggleImageUploadPopup(request: LocalEvent, store: PatientStore): void {
        let patientUIModel = store.PatientUIModels[request.PatientId];
        let bannerModel = store.PatientBannerModels[request.PatientId];

        patientUIModel = patientUIModel.set("ShowProfileImagePopup", !patientUIModel.get("ShowProfileImagePopup"));
        bannerModel = bannerModel.set("PatientDropdownVisible", false);

        store.PatientBannerModels[request.PatientId] = bannerModel;
        store.PatientUIModels[request.PatientId] = patientUIModel;
    }

    private PrintSummary(request: LocalEvent, store: PatientStore): void {
        store.ActionsProfile.printSummary(request.PatientId, store);
    }

    private SaveMemoClick(request: LocalEvent, store: PatientStore): void {
        let value = request.Get<string>("value");
        let uiModel = store.PatientUIModels[request.PatientId];

        let contactId = uiModel.get("PatientContactId");
        let contactFacetId = uiModel.get("PatientContactFacetId");
        store.ActionsProfile.saveMemo(value, contactId, contactFacetId);
    }

    private DeleteMemoClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        let contactId = uiModel.get("PatientContactId");
        let contactFacetId = uiModel.get("PatientContactFacetId");
        store.ActionsProfile.saveMemo(null, contactId, contactFacetId, true);
    }

    private AddMemoClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        let bannerModel = store.PatientBannerModels[request.PatientId];
        let patientModel = store.PatientModels[request.PatientId];

        uiModel = uiModel.set("ShowProfileMemoPopup", true);
        bannerModel = bannerModel.set("PatientDropdownVisible", false);

        if (patientModel.IndividualResponse.MemoNote)
            uiModel = uiModel.set("IsProfileMemoEditMode", false);
        else
            uiModel = uiModel.set("IsProfileMemoEditMode", true);

        store.PatientBannerModels[request.PatientId] = bannerModel;
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private EditMemoClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("IsProfileMemoEditMode", true);
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private CancelMemoClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("ShowProfileMemoPopup", false);
        uiModel = uiModel.set("IsProfileMemoEditMode", false);

        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private CancelEditMemoClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("IsProfileMemoEditMode", false);
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private changeProfileTab(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];

        let editMode = uiModel.get("PatientProfileEditMode");

        if (editMode === EditMode.Edit) {
            // undo whatever was being done in the current tab
            let currentTab: ProfileTab = uiModel.get("PatientProfileActiveTab");
            switch (currentTab) {
                case ProfileTab.Clinicians:
                    store.PatientProfileCliniciansHandler.CliniciansCancelClick(request, store);
                    break;
                case ProfileTab.Contacts:
                    store.PatientProfileContactsHandler.contactsCancelClick(request, store);
                    break;
                case ProfileTab.Demographic:
                    store.PatientProfileDemographicsHandler.DemographicsCancelClick(request, store);
                    break;
                case ProfileTab.Identifiers:
                    store.PatientProfileIdentifiersHandler.IdentifiersCancelClick(request, store);
                    break;
                case ProfileTab.Other:
                    store.PatientProfileOtherInfoHandler.OtherInfoCancelClick(request, store);
                    break;
                case ProfileTab.Relationships:
                    store.PatientProfileRelationshipsHandler.cancelRelationshipClick(request, store);
                    break;

                default:
                    break;
            }
        }

        let value = request.Get<number>("value");
        uiModel = uiModel.set("PatientProfileActiveTab", value);
        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private NAMLabelClick(request: LocalEvent, store: PatientStore): void {
        store.PatientProfileIdentifiersHandler.IdentifiersEditClick(request, store);

        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("ActiveVerticalTab", PatientTab.Profile);
        uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.Identifiers);
        if (patientStore.CanEditPatient) {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
            uiModel = uiModel.set("PatientBannerFocusOperation", ExpressEditMode.NAM);
        }
        else {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);
        }
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private DOBLabelClick(request: LocalEvent, store: PatientStore): void {
        store.PatientProfileDemographicsHandler.DemographicsEditClick(request, store);

        let uiModel = store.PatientUIModels[request.PatientId];

        uiModel = uiModel.set("ActiveVerticalTab", PatientTab.Profile);
        uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.Demographic);
        if (patientStore.CanEditPatient) {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
            uiModel = uiModel.set("PatientBannerFocusOperation", ExpressEditMode.DOB);
        }
        else {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);
        }
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private PhoneLabelClick(request: LocalEvent, store: PatientStore): void {
        store.PatientProfileContactsHandler.contactsEditClick(request, store);

        let uiModel = store.PatientUIModels[request.PatientId];
        let contactPointId = request.Get<string>("contactPointId");

        uiModel = uiModel.set("ActiveVerticalTab", PatientTab.Profile);
        uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.Contacts);
        if (patientStore.CanEditPatient) {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
            uiModel = uiModel.set("PatientBannerFocusOperation", ExpressEditMode.Phone);
            uiModel = uiModel.set("PatientBannerFocusOperationTarget", contactPointId);
        }
        else {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);
        }

        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private AddressLabelClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];

        let contactPointId = request.Get<string>("contactPointId");

        store.PatientProfileContactsHandler.contactsEditClick(request, store);

        uiModel = uiModel.set("ActiveVerticalTab", PatientTab.Profile);
        uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.Contacts);
        if (patientStore.CanEditPatient) {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
            uiModel = uiModel.set("PatientBannerFocusOperation", ExpressEditMode.Address);
            uiModel = uiModel.set("PatientBannerFocusOperationTarget", contactPointId);
        }
        else {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);
        }
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private EmailLabelClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];

        let contactPointId = request.Get<string>("contactPointId");

        store.PatientProfileContactsHandler.contactsEditClick(request, store);

        uiModel = uiModel.set("ActiveVerticalTab", PatientTab.Profile);
        uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.Contacts);
        if (patientStore.CanEditPatient) {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
            uiModel = uiModel.set("PatientBannerFocusOperation", ExpressEditMode.Email);
            uiModel = uiModel.set("PatientBannerFocusOperationTarget", contactPointId);
        }
        else {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);
        }
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private PatientFirstNameLabelClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];

        store.PatientProfileDemographicsHandler.DemographicsEditClick(request, store);

        uiModel = uiModel.set("ActiveVerticalTab", PatientTab.Profile);
        uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.Demographic);
        if (patientStore.CanEditPatient) {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
            uiModel = uiModel.set("PatientBannerFocusOperation", ExpressEditMode.PatientFirstName);
        }
        else {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);
        }
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private PatientLastNameLabelClick(request: LocalEvent, store: PatientStore): void {
        this.openPatientTab(request, store, PatientTab.Profile, ProfileTab.Demographic, ExpressEditMode.PatientLastName);
    }

    private PatientGenderLabelClick(request: LocalEvent, store: PatientStore): void {
        this.openPatientTab(request, store, PatientTab.Profile, ProfileTab.Demographic);
    }

    private PreferredIntervenorOnFileClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("ActiveVerticalTab", PatientTab.Profile);
        uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.Clinicians);
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private EnrollmentStatusLabelCLick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];

        store.PatientProfileContactsHandler.contactsEditClick(request, store);

        uiModel = uiModel.set("ActiveVerticalTab", PatientTab.Profile);
        uiModel = uiModel.set("PatientProfileActiveTab", ProfileTab.Enrollment);
        if (patientStore.CanEditPatient) {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
            uiModel = uiModel.set("PatientBannerFocusOperation", ExpressEditMode.Inscription);
        }
        else {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);
        }
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private ClearExpressEdit(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];

        uiModel = uiModel.set("PatientBannerFocusOperation", null);

        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private DeactivatePatientTogglePopup(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("ShowDeactivatePopup", !uiModel.get("ShowDeactivatePopup"));
        let bannerModel = store.PatientBannerModels[request.PatientId];
        bannerModel = bannerModel.set("PatientDropdownVisible", false);
        store.PatientUIModels[request.PatientId] = uiModel;
        store.PatientBannerModels[request.PatientId] = bannerModel;
    }

    private DeactivatePatientNoteChange(request: LocalEvent, store: PatientStore): void {
        let value = request.Get<string>("value");
        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("DeactivationNote", value);
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private DeactivatePatientReasonChange(request: LocalEvent, store: PatientStore): void {
        let value = request.Get<string>("value");

        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("SelectedDeactivationReasonLookupId", value);
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private DeactivatePatient(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        let patientModel = store.PatientModels[request.PatientId];

        let patientContactId = request.PatientId;
        let reasonId = uiModel.get("SelectedDeactivationReasonLookupId");
        let reasonNote = uiModel.get("DeactivationNote");

        if (patientModel.InactivableResponse && patientModel.InactivableResponse.IsInactive) {
            uiModel = uiModel.set("ShowDeactivatePopup", false);
            store.PatientUIModels[request.PatientId] = uiModel;
            store.ActionsProfile.reactivatePatient(patientContactId);
        }
        else {
            store.ActionsProfile.deactivatePatient(patientContactId, reasonId, reasonNote);
        }
    }

    private TogglePatientHeaderDropdownMenu(request: LocalEvent, store: PatientStore): void {
        let bannerModel = store.PatientBannerModels[request.PatientId];
        let visible = !bannerModel.get("PatientDropdownVisible");
        bannerModel = bannerModel.set("PatientDropdownVisible", visible);
        store.PatientBannerModels[request.PatientId] = bannerModel;
    }

    private OnOverlayContainerClick(request: LocalEvent, store: PatientStore): void {
        let bannerModel = store.PatientBannerModels[request.PatientId];

        if (bannerModel != null && bannerModel.get("PatientDropdownVisible")) {
            bannerModel = bannerModel.set("PatientDropdownVisible", !bannerModel.get("PatientDropdownVisible"));
            store.PatientBannerModels[request.PatientId] = bannerModel;
            store.EmitChangeToListeners();
        }
    }

    private deleteProfilePic(request: LocalEvent, store: PatientStore): void {
        let profilePicture = request.Get<ProfilePicture>("ProfilePicture");
        store.ActionsProfile.removeProfilePicEmployee(profilePicture.Id);
    }

    private showNewExternalMessagePopup(request: LocalEvent, store: PatientStore): void {
        const show = request.Get<boolean>("value");
        let uiModel = store.PatientUIModels[request.PatientId];
        let bannerModel = store.PatientBannerModels[request.PatientId];

        uiModel = uiModel.set("ShowNewExternalMessagePopup", show);
        bannerModel = bannerModel.set("PatientDropdownVisible", false);

        store.PatientBannerModels[request.PatientId] = bannerModel;
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private openPatientTab(
        request: LocalEvent,
        store: PatientStore,
        patientTab: PatientTab,
        profileTab: ProfileTab,
        field?: ExpressEditMode
    ): void {
        store.PatientProfileDemographicsHandler.DemographicsEditClick(request, store);

        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel
            .set("ActiveVerticalTab", patientTab)
            .set("PatientProfileActiveTab", profileTab);

        if (patientStore.CanEditPatient) {
            uiModel = uiModel
                .set("PatientProfileEditMode", EditMode.Edit)
                .set("PatientBannerFocusOperation", field);
        }
        else {
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);
        }
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private showRedActiveNoteTab(request: LocalEvent, store: PatientStore): void {
        const encounterId = request.Get<string>("encounterId");

        store.PatientUIModels[request.PatientId] = store.PatientUIModels[request.PatientId].set("ShowRedNoteActiveTab", true);
        store.ActionsMedicalNote.LoadEncounterForView(encounterId, store.getCurrentPatientContactId(), SubServerOperations.MedicalNote.ForGrowthChart);
    }

    private showPopulationBasedFundingTab(request: LocalEvent, store: PatientStore): void {
        store.PatientUIModels[request.PatientId] = store.PatientUIModels[request.PatientId]
            .set("ActiveVerticalTab", PatientTab.Profile)
            .set("PatientProfileActiveTab", ProfileTab.PopulationBasedFunding);
    }

    private updatePatientMandatoryFields(request: LocalEvent, store: PatientStore): void {
        const contactId = request.Get<string>("contactId");
        const fields = request.Get<{
            firstName?: string;
            lastName?: string;
            sexAtBirthLookupId?: string;
            dateOfBirth?: Date;
        }>("fields");
        // refresh current patient models with freshly updated ones
        let uiModel = store.PatientUIModels[contactId];
        let patientModel = store.PatientModels[contactId];
        let patientDemographicModel = store.PatientProfileDemographicModels[contactId];

        patientDemographicModel = patientDemographicModel.set("FirstName", fields.firstName || patientModel.IndividualResponse.FirstName);
        patientDemographicModel = patientDemographicModel.set("LastName", fields.lastName || patientModel.IndividualResponse.LastName);
        patientDemographicModel = patientDemographicModel.set("DateOfBirth", fields.dateOfBirth || patientModel.IndividualResponse.DateOfBirth);
        patientDemographicModel = patientDemographicModel.set("SelectedGenderLookupId", fields.sexAtBirthLookupId || patientModel.IndividualResponse.GenderLookupId);

        patientModel.IndividualResponse.FirstName = fields.firstName || patientModel.IndividualResponse.FirstName;
        patientModel.IndividualResponse.LastName = fields.lastName || patientModel.IndividualResponse.LastName;
        patientModel.IndividualResponse.DateOfBirth = fields.dateOfBirth || patientModel.IndividualResponse.DateOfBirth;
        patientModel.IndividualResponse.GenderLookupId = fields.sexAtBirthLookupId || patientModel.IndividualResponse.GenderLookupId;

        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        let bannerModel = store.PatientBannerModels[contactId];
        bannerModel = bannerModel.delete("Model");
        bannerModel = bannerModel.set("Model", patientModel);

        store.PatientBannerModels[contactId] = bannerModel;
        store.PatientProfileDemographicModels[contactId] = patientDemographicModel;
        store.PatientUIModels[contactId] = uiModel;
        store.PatientModels[contactId] = patientModel;
    }

    private togglePatientMergeRequestPopup(request: LocalEvent, store: PatientStore): void {
        const show = request.Get<boolean>("showPopup");
        const contactId = request.Get<string>("contactId");
        store.PatientBannerModels[contactId] = store.PatientBannerModels[contactId].set("PatientDropdownVisible", false);
        store.PatientUIModels[contactId] = store.PatientUIModels[contactId].set("ShowPatientMergeRequestPopup", show);
    }

    private toggleCancelPatientMergeRequestPopup(request: LocalEvent, store: PatientStore): void {
        const show = request.Get<boolean>("showPopup");
        const contactId = request.Get<string>("contactId");
        store.PatientBannerModels[contactId] = store.PatientBannerModels[contactId].set("PatientDropdownVisible", false);
        store.PatientUIModels[contactId] = store.PatientUIModels[contactId].set("ShowPatientMergeRequestCancellingPopup", show);
    }

    private handlePatientMergeRequestResponse(request: LocalEvent, store: PatientStore): void {
        const mergeToPatientContactId = request.Get<string>("mergeToPatientContactId");
        const mergeFromPatientContactId = request.Get<string>("mergeFromPatientContactId");

        if (store.PatientUIModels[mergeToPatientContactId]) {
            store.PatientUIModels[mergeToPatientContactId] = store.PatientUIModels[mergeToPatientContactId].set("ShowPatientMergeRequestPopup", false);
            store.PatientBannerModels[mergeToPatientContactId] = store.PatientBannerModels[mergeToPatientContactId].set("IsPartOfMergeRequest", true);
        }

        if (store.PatientUIModels[mergeFromPatientContactId]) {
            store.PatientUIModels[mergeFromPatientContactId] = store.PatientUIModels[mergeFromPatientContactId].set("ShowPatientMergeRequestPopup", false);
            store.PatientBannerModels[mergeFromPatientContactId] = store.PatientBannerModels[mergeFromPatientContactId].set("IsPartOfMergeRequest", true);
        }
    }

    private handlePatientMergeCancellationRequestResponse(request: LocalEvent, store: PatientStore): void {
        const mergeToPatientContactId = request.Get<string>("mergeToPatientContactId");
        const mergeFromPatientContactId = request.Get<string>("mergeFromPatientContactId");

        if (store.PatientUIModels[mergeToPatientContactId]) {
            store.PatientUIModels[mergeToPatientContactId] = store.PatientUIModels[mergeToPatientContactId].set("ShowPatientMergeRequestCancellingPopup", false);
            store.PatientBannerModels[mergeToPatientContactId] = store.PatientBannerModels[mergeToPatientContactId].set("IsPartOfMergeRequest", false);
        }

        if (store.PatientUIModels[mergeFromPatientContactId]) {
            store.PatientUIModels[mergeFromPatientContactId] = store.PatientUIModels[mergeFromPatientContactId].set("ShowPatientMergeRequestCancellingPopup", false);
            store.PatientBannerModels[mergeFromPatientContactId] = store.PatientBannerModels[mergeFromPatientContactId].set("IsPartOfMergeRequest", false);
        }
    }

    private MarkTestPatientTogglePopup(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        uiModel = uiModel.set("ShowMarkTestPatientPopup", !uiModel.get("ShowMarkTestPatientPopup"));
        let bannerModel = store.PatientBannerModels[request.PatientId];
        bannerModel = bannerModel.set("PatientDropdownVisible", false);
        store.PatientUIModels[request.PatientId] = uiModel;
        store.PatientBannerModels[request.PatientId] = bannerModel;
    }

    private MarkTestPatient(request: LocalEvent, store: PatientStore): void {
        let patientModel = store.PatientModels[request.PatientId];

        let patientContactId = request.PatientId;
        const isTest = !patientModel.PatientResponse.IsTest;
        store.ActionsProfile.markTestPatient(patientContactId, isTest);
    }
}

export { PatientProfileHandler };
